import { Component, OnInit, OnDestroy, Inject, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { bmxurl } from 'src/globals';
import { cTillSale } from 'src/types/tillsale';
import { GlobalService } from '../../_services/global.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-kitchendisplaysystem',
  templateUrl: './kitchendisplaysystem.component.html',
  styleUrls: ['./kitchendisplaysystem.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class KitchendisplaysystemComponent implements OnInit, OnDestroy {
  VenueID: string = localStorage.getItem("SmartVenueID")!;
  httpResponse: any;
  allSales: cTillSale[] = [];
  completedIds: Set<string> = new Set();
  priorityIds: Set<string> = new Set();

  showNotesDialog: boolean = false;
  notesOrder: cTillSale | null = null;
  notesText: string = '';
  showCompleted: boolean = false;
  get viewOptions() {
    return [
      { label: `Open  ${this.openOrders.length}`, value: false },
      { label: `Completed  ${this.completedOrders.length}`, value: true }
    ];
  }
  currentTime: Date = new Date();
  productMap: Map<string, string> = new Map();
  optionsMap: Map<string, any[]> = new Map();

  private broadcastChannel: BroadcastChannel;
  private kitchenBroadcast: BroadcastChannel;
  private clockInterval: any;

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private globalService: GlobalService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.globalService.setSmallScreenMenu(false);
    this.globalService.setKdsMode(true);

    const stored = localStorage.getItem('kds_completed_' + this.VenueID);
    if (stored) {
      const parsed: string[] = JSON.parse(stored);
      // Only keep completions from today
      const todayKey = this.getTodayVenueDate();
      const storedDate = localStorage.getItem('kds_completed_date_' + this.VenueID);
      if (storedDate === todayKey) {
        this.completedIds = new Set(parsed);
      } else {
        localStorage.removeItem('kds_completed_' + this.VenueID);
      }
    }

    const storedPriority = localStorage.getItem('kds_priority_' + this.VenueID);
    if (storedPriority) {
      this.priorityIds = new Set(JSON.parse(storedPriority));
    }

    this.broadcastChannel = new BroadcastChannel('till_updates');
    this.broadcastChannel.onmessage = () => this.loadSales();
    this.kitchenBroadcast = new BroadcastChannel('kitchen_updates');

    this.loadProducts();
    this.loadSales();
    this.clockInterval = setInterval(() => this.currentTime = new Date(), 1000);
  }

  ngOnDestroy(): void {
    this.broadcastChannel.close();
    this.kitchenBroadcast.close();
    if (this.clockInterval) clearInterval(this.clockInterval);
    this.globalService.setKdsMode(false);
  }

  exitKDS(): void {
    this.globalService.setKdsMode(false);
    const doc = this.document as any;
    if (doc.exitFullscreen) {
      doc.exitFullscreen().catch(() => {});
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    }
    this.router.navigateByUrl('/till');
  }

  private getTodayVenueDate(): string {
    const now = new Date();
    const day = ('00' + now.getDate()).slice(-2);
    const month = ('00' + (now.getMonth() + 1)).slice(-2);
    const year = now.getFullYear().toString();
    return this.VenueID + "_" + day + month + year;
  }

  // + "/" + venueDate

  loadProducts(): void {
    this.http.get(bmxurl + "TillProductList/" + this.VenueID + "/false").subscribe({
      next: (response: any) => {
        if (response.succeeded && response.data) {
          const products: any[] = response.data;
          this.productMap = new Map(products.map(p => [p.id, p.name]));
          this.optionsMap = new Map(products.map(p => [p.id, p.options ?? []]));
        }
      },
      error: () => {}
    });
  }

  loadSales(): void {
    this.http.get(bmxurl + "TillSaleList/" + this.VenueID).subscribe({
      next: (response: any) => {
        this.httpResponse = response;
        if (this.httpResponse.succeeded) {
          const raw: any[] = this.httpResponse.data ?? [];
          if (raw.length > 0) {
            if (raw[0].products?.length > 0) {
            }
          }
          this.allSales = raw
            .filter(s => !s.voided && s.products?.some((p: any) => p.kitchenitem))
            .map((s: any) => ({
              ...s,
              products: (s.products ?? []).map((p: any) => ({
                ...p,
                selectedOptions: p.selectedOptions?.length ? p.selectedOptions
                               : p.SelectedOptions?.length ? p.SelectedOptions
                               : p.options?.length ? p.options
                               : p.Options?.length ? p.Options
                               : []
              }))
            }))
            .sort((a, b) => new Date(a.createddate).getTime() - new Date(b.createddate).getTime());
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load kitchen orders.' });
      }
    });
  }

  get openOrders(): cTillSale[] {
    return this.allSales
      .filter(s => !this.completedIds.has(s.id))
      .sort((a, b) => {
        const ap = this.priorityIds.has(a.id) ? 0 : 1;
        const bp = this.priorityIds.has(b.id) ? 0 : 1;
        return ap - bp;
      });
  }

  get completedOrders(): cTillSale[] {
    return this.allSales.filter(s => this.completedIds.has(s.id));
  }

  get displayedOrders(): cTillSale[] {
    return this.showCompleted ? this.completedOrders : this.openOrders;
  }

  get groupedDisplayedOrders(): Array<{ tablenumber: string | null, orders: cTillSale[], isGroup: boolean }> {
    const orders = this.displayedOrders;
    const countMap = new Map<string, number>();
    orders.forEach(o => {
      if (o.tablenumber) {
        countMap.set(o.tablenumber, (countMap.get(o.tablenumber) || 0) + 1);
      }
    });

    const groups: Array<{ tablenumber: string | null, orders: cTillSale[], isGroup: boolean }> = [];
    const processed = new Set<string>();

    orders.forEach(order => {
      if (order.tablenumber && (countMap.get(order.tablenumber) ?? 0) > 1) {
        if (!processed.has(order.tablenumber)) {
          processed.add(order.tablenumber);
          groups.push({
            tablenumber: order.tablenumber,
            orders: orders.filter(o => o.tablenumber === order.tablenumber),
            isGroup: true
          });
        }
      } else {
        groups.push({ tablenumber: order.tablenumber || null, orders: [order], isGroup: false });
      }
    });

    return groups;
  }

  kitchenItemsFor(sale: cTillSale): any[] {
    return sale.products.filter(p => (p.kitchenitem || p.product?.kitchenitem) && (p.qty ?? 1) > 0);
  }

  getItemName(item: any): string {
    // First try the product map (most reliable — products loaded separately)
    const mapName = this.productMap.get(item.id) ?? this.productMap.get(item.productId) ?? this.productMap.get(item.product?.id);
    if (mapName) return mapName;
    // Fall back to whatever the API embedded on the item
    return item.product?.name ?? item.product?.Name
      ?? item.name ?? item.Name
      ?? item.productName ?? item.ProductName
      ?? '';
  }

  getItemOptions(item: any): any[] {
    return item.selectedOptions ?? item.SelectedOptions
        ?? item.options ?? item.Options
        ?? [];
  }

  markPriority(sale: cTillSale): void {
    if (this.priorityIds.has(sale.id)) {
      this.priorityIds.delete(sale.id);
    } else {
      this.priorityIds.add(sale.id);
    }
    localStorage.setItem('kds_priority_' + this.VenueID, JSON.stringify(Array.from(this.priorityIds)));
  }

  isPriority(sale: cTillSale): boolean {
    return this.priorityIds.has(sale.id);
  }

  markComplete(sale: cTillSale): void {
    sale.kitchencomplete = true;
    this.http.put<any>(bmxurl + "tillsale", sale).subscribe({
      next: (response) => {
        if (!response.succeeded) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not mark order complete: ' + (response.messages?.[0] ?? 'Unknown error') });
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not mark order complete.' });
      }
    });
    this.priorityIds.delete(sale.id);
    localStorage.setItem('kds_priority_' + this.VenueID, JSON.stringify(Array.from(this.priorityIds)));
    this.completedIds.add(sale.id);
    const todayKey = this.getTodayVenueDate();
    localStorage.setItem('kds_completed_' + this.VenueID, JSON.stringify(Array.from(this.completedIds)));
    localStorage.setItem('kds_completed_date_' + this.VenueID, todayKey);
    this.kitchenBroadcast.postMessage({
      type: 'order_complete',
      name: this.getOrderDisplayName(sale),
      tableNumber: sale.tablenumber ?? ''
    });
  }

  markReopen(sale: cTillSale): void {
    sale.kitchencomplete = false;
    this.http.put<any>(bmxurl + "tillsale", sale).subscribe({
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not reopen order.' });
      }
    });
    this.completedIds.delete(sale.id);
    localStorage.setItem('kds_completed_' + this.VenueID, JSON.stringify(Array.from(this.completedIds)));
  }

  getElapsedMinutes(createdDate: Date): number {
    const created = new Date(createdDate).getTime();
    const now = this.currentTime.getTime();
    return Math.floor((now - created) / 60000);
  }

  getElapsedLabel(createdDate: Date): string {
    const totalSeconds = Math.floor((this.currentTime.getTime() - new Date(createdDate).getTime()) / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins < 60) return mins + 'm ' + secs + 's';
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return hrs + 'h ' + rem + 'm';
  }

  isAmended(order: any): boolean {
    if (!order.lastupdated || !order.createddate) return false;
    const updated = new Date(order.lastupdated).setMilliseconds(0);
    const created = new Date(order.createddate).setMilliseconds(0);
    return updated !== created;
  }

  getCardUrgencyClass(createdDate: Date): string {
    const mins = this.getElapsedMinutes(createdDate);
    if (mins >= 15) return 'card-urgent';
    if (mins >= 5) return 'card-warning';
    return 'card-fresh';
  }

  openNotes(sale: cTillSale): void {
    this.notesOrder = sale;
    this.notesText = sale.notes || '';
    this.showNotesDialog = true;
  }

  saveNotes(): void {
    if (!this.notesOrder) return;
    this.notesOrder.notes = this.notesText;
    this.http.put<any>(bmxurl + "/tillsale", this.notesOrder).subscribe({
      next: (response) => {
        if (response.succeeded) {
          this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Notes saved.' });
          if (response.data) {
            const idx = this.allSales.findIndex(s => s.id === this.notesOrder.id);
            if (idx !== -1) this.allSales[idx] = response.data;
          }
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: response.messages?.[0] || 'Could not save notes.' });
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not save notes.' })
    });
    this.showNotesDialog = false;
    this.notesOrder = null;
  }

  getOrderDisplayName(sale: any): string {
    const tillHr = sale.tillsaleidhr ?? sale.TillSaleIdHR ?? sale.TillSaleIdHr ?? sale.tillSaleIdHR;
    if (tillHr != null && String(tillHr).trim().length > 0) return String(tillHr).trim();
    const bookingHr = sale.bookingIdHR ?? sale.BookingIdHR ?? sale.bookingIdHr;
    if (bookingHr != null && String(bookingHr).trim().length > 0) return String(bookingHr).trim();
    const sid = sale.salesid ?? sale.SalesId ?? sale.salesId;
    if (sid != null && String(sid).trim().length > 0) return '#' + String(sid).trim();
    const notes = sale.notes ?? sale.Notes;
    if (notes && String(notes).trim().length > 0) return String(notes).trim();
    return 'Order';
  }
}
